<template>
  <div class="flex h-full min-h-0 min-w-0 bg-[#f1f4f4]">
    <div class="flex min-w-0 flex-1 flex-col">
      <WriterToolbar v-if="editor" ref="toolbar" :editor="editor" class="shrink-0" />

      <div v-if="notice" class="flex shrink-0 items-center gap-3 px-6 py-2 text-xs font-medium" :class="notice.tone" :role="notice.alert ? 'alert' : 'status'">
        <span class="min-w-0 flex-1">{{ notice.text }}</span>
        <button v-if="notice.action" type="button" class="min-h-8 shrink-0 rounded-lg bg-white/70 px-3 font-bold ring-1 ring-black/5" @click="notice.action.run">{{ notice.action.label }}</button>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div class="flex justify-center px-4 py-8">
          <div ref="paper" class="writer-paper relative min-h-[calc(100vh-200px)] w-full max-w-[820px] rounded-sm bg-white px-8 py-12 shadow-[0_1px_3px_rgba(28,72,72,0.12),0_12px_32px_-12px_rgba(28,72,72,0.15)] sm:px-[72px]">
            <h1 class="mb-6 font-display text-4xl font-bold leading-tight text-[#1c4848]">{{ pagesStore.selectedPage?.title }}</h1>
            <div v-if="loading" class="space-y-3" aria-label="Loading the page"><div v-for="n in 6" :key="n" class="h-4 animate-pulse rounded bg-[#1c4848]/5" :class="n % 3 === 0 ? 'w-2/3' : 'w-full'"></div></div>
            <div v-else-if="loadError" role="alert" class="py-12 text-center">
              <p class="mb-3 text-sm text-[#1c4848]/60">The page could not be loaded.</p>
              <button type="button" class="min-h-11 rounded-xl bg-[#1c4848] px-5 text-xs font-bold text-white" @click="load">Try again</button>
            </div>
            <template v-if="editor">
              <EditorContent v-show="!loading && !loadError" :editor="editor" />
              <BlockGutter v-if="paper && !loading && !locked" :editor="editor" :paper="paper" class="hidden sm:flex" />
              <BubbleMenu :editor="editor" :tippy-options="{ duration: 100 }" :should-show="showStyleMenu">
                <div class="flex items-center gap-0.5 rounded-xl bg-white p-1 shadow-xl ring-1 ring-[#1c4848]/10">
                  <ToolButton label="Bold" icon="solar:text-bold-linear" :active="editor.isActive('bold')" @click="editor.chain().focus().toggleBold().run()" />
                  <ToolButton label="Italic" icon="solar:text-italic-linear" :active="editor.isActive('italic')" @click="editor.chain().focus().toggleItalic().run()" />
                  <ToolButton label="Underline" icon="solar:text-underline-linear" :active="editor.isActive('underline')" @click="editor.chain().focus().toggleUnderline().run()" />
                  <ToolButton label="Highlight" icon="solar:pen-new-round-linear" :active="editor.isActive('highlight')" @click="editor.chain().focus().toggleHighlight().run()" />
                  <ToolButton label="Link" icon="solar:link-linear" :active="editor.isActive('link')" @click="toolbar?.openLink()" />
                </div>
              </BubbleMenu>
            </template>
          </div>
        </div>
      </div>

      <footer class="flex h-9 shrink-0 items-center justify-between gap-4 border-t border-[#1c4848]/5 bg-white px-4 text-[11px] font-medium text-[#1c4848]/55">
        <span aria-live="polite" class="flex items-center gap-1.5">
          <span class="h-1.5 w-1.5 rounded-full" :class="SAVE_DOT[saveState]" aria-hidden="true"></span>{{ saveLabel }}
        </span>
        <span class="tabular-nums">{{ words }} {{ words === 1 ? 'word' : 'words' }} · {{ minutes }} min read</span>
      </footer>
    </div>

    <aside v-if="panel" class="flex shrink-0 flex-col border-l border-[#1c4848]/5 bg-[#fafbfc]" :class="panel === 'phone' ? 'w-[440px]' : 'w-[340px]'" :aria-label="currentPanel.label">
      <h2 v-if="panel !== 'phone'" class="shrink-0 border-b border-[#1c4848]/5 px-4 py-3 text-xs font-bold text-[#1c4848]">{{ currentPanel.label }}</h2>
      <div class="min-h-0 flex-1" :class="panel === 'phone' ? '' : 'overflow-y-auto'">
        <InsertPanel v-if="panel === 'insert' && editor" :editor="editor" :read-only="locked" />
        <OutlinePanel v-else-if="panel === 'outline' && editor" :editor="editor" :version="version" />
        <PhonePanel v-else-if="panel === 'phone'" :document="liveDocument" :title="pagesStore.selectedPage?.title" :description="pagesStore.selectedPage?.description" :book-name="bookName" :language-id="languageId" />
        <HistoryPanel v-else-if="panel === 'history'" :page-id="pageId" :version="revision" :read-only="locked" @restore="restore" />
        <PublishPanel v-else-if="panel === 'publish'" :document="liveDocument" :rows="rows" :language-id="languageId" />
        <PodcastSidebar v-else-if="panel === 'podcasts'" />
        <HadithsSidebar v-else-if="panel === 'hadiths'" />
      </div>
    </aside>

    <nav class="flex w-14 shrink-0 flex-col items-center gap-1 border-l border-[#1c4848]/5 bg-white py-3" aria-label="Writer panels">
      <button
        v-for="option in PANELS"
        :key="option.key"
        type="button"
        class="flex h-10 w-10 items-center justify-center rounded-xl text-xl transition-colors"
        :class="panel === option.key ? 'bg-[#1c4848] text-white' : 'text-[#1c4848]/55 hover:bg-[#1c4848]/5 hover:text-[#1c4848]'"
        :aria-label="option.label"
        :aria-pressed="panel === option.key"
        :title="option.label"
        @click="panel = panel === option.key ? null : option.key"
      ><iconify-icon :icon="option.icon" aria-hidden="true" /></button>
    </nav>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/vue-3';
import { TextSelection } from '@tiptap/pm/state';
import BlockGutter from '@/components/writer/BlockGutter.vue';
import ToolButton from '@/components/writer/ToolButton.vue';
import WriterToolbar from '@/components/writer/WriterToolbar.vue';
import { writerExtensions } from '@/components/writer/writerExtensions.js';
import '@/components/writer/writer.css';
import InsertPanel from '@/components/writer/panels/InsertPanel.vue';
import OutlinePanel from '@/components/writer/panels/OutlinePanel.vue';
import PhonePanel from '@/components/writer/panels/PhonePanel.vue';
import HistoryPanel from '@/components/writer/panels/HistoryPanel.vue';
import PublishPanel from '@/components/writer/panels/PublishPanel.vue';
import PodcastSidebar from '@/components/app/dashboard/sidebars/PodcastSidebar.vue';
import HadithsSidebar from '@/components/app/dashboard/sidebars/HadithsSidebar.vue';
import { useContentFeedStore } from '@/store/content-feed';
import { useBooksStore } from '@/store/books/index.js';
import { usePagesStore } from '@/store/pages/index.js';
import { usePageContentsStore } from '@/store/page-contents/index.js';

const SAVE_AFTER_MS = 1200;
const RETRY_AFTER_MS = 5000;
const PANELS = [
  { key: 'insert', label: 'Components', icon: 'solar:widget-add-linear' },
  { key: 'phone', label: 'On the phone', icon: 'solar:smartphone-linear' },
  { key: 'outline', label: 'Outline', icon: 'solar:list-linear' },
  { key: 'history', label: 'Version history', icon: 'solar:history-linear' },
  { key: 'publish', label: 'Post to feed', icon: 'solar:square-share-line-linear' },
  { key: 'podcasts', label: 'Podcasts', icon: 'solar:microphone-3-linear' },
  { key: 'hadiths', label: 'Hadiths', icon: 'solar:chat-square-linear' },
];
const SAVE_DOT = { saved: 'bg-emerald-600', dirty: 'bg-[#a0833c]', saving: 'bg-[#a0833c] animate-pulse', error: 'bg-red-600', conflict: 'bg-red-600', invalid: 'bg-red-600' };

const route = useRoute();
const pagesStore = usePagesStore();
const booksStore = useBooksStore();
const contents = usePageContentsStore();
const feeds = useContentFeedStore();

const paper = ref(null);
const toolbar = ref(null);
const panel = ref('insert');
const loading = ref(true);
const loadError = ref(false);
const locked = ref(false);
const written = ref(false);
const revision = ref(0);
const rows = ref({});
const version = ref(0);
const liveDocument = ref(null);
const saveState = ref('saved');
const invalidMessage = ref('');
const savedAt = ref(null);

const pageId = computed(() => route.params.pageId);
const languageId = computed(() => pagesStore.selectedPage?.language_id ?? 1);
const bookName = computed(() => (booksStore.books ?? []).find((book) => String(book.id) === String(route.params.bookId))?.name ?? '');

let baseline = '';
let timer = null;
let inFlight = null;
let openPageId = null;

const editor = useEditor({
  extensions: writerExtensions({
    search: (params) => feeds.searchStudio({ ...params, language_id: languageId.value }),
    uploadImage: (file) => contents.uploadDocumentImage(openPageId, file),
  }),
  editorProps: {
    attributes: { class: 'writer-content', 'aria-label': 'Page text' },
  },
  onUpdate: onEdited,
  onSelectionUpdate: () => { version.value += 1; },
});

const words = computed(() => {
  void version.value;
  const text = liveDocument.value ? JSON.stringify(wordsOf(liveDocument.value)) : '';
  return (text.match(/[\p{L}\p{N}]+/gu) ?? []).length;
});
const minutes = computed(() => Math.max(1, Math.round(words.value / 180)));
const currentPanel = computed(() => PANELS.find((p) => p.key === panel.value));

const saveLabel = computed(() => ({
  saved: savedAt.value ? `Saved at ${savedAt.value}` : (written.value || locked.value ? 'All changes saved' : 'Unchanged'),
  dirty: 'Unsaved changes',
  saving: 'Saving…',
  error: 'Could not save. Trying again…',
  conflict: 'Not saved',
  invalid: 'Not saved',
}[saveState.value]));

const notice = computed(() => {
  if (locked.value) return { tone: 'bg-[#ecd79f]/30 text-[#7a6128]', text: 'The Qur\'an books are generated, so this page can be read here but not changed.' };
  if (saveState.value === 'conflict') return { alert: true, tone: 'bg-red-50 text-red-800', text: 'This page was changed in another window. Your last edits here were not saved.', action: { label: 'Load the latest', run: load } };
  if (saveState.value === 'invalid') return { alert: true, tone: 'bg-red-50 text-red-800', text: invalidMessage.value };
  if (saveState.value === 'error') return { alert: true, tone: 'bg-red-50 text-red-800', text: 'Your connection dropped. Keep this tab open: the page saves as soon as it can.' };
  return null;
});

/** Only over selected words: a selected component has its own controls. */
function showStyleMenu({ editor: e, state }) {
  return e.isEditable && state.selection instanceof TextSelection && !state.selection.empty;
}

function wordsOf(node) {
  const own = Object.entries(node.attrs ?? {}).flatMap(([key, value]) => (['id', 'src', 'image', 'alt', 'kind', 'style', 'level', 'textAlign', 'correct', 'repeat', 'start', 'height', 'position', 'side', 'tone', 'layout', 'look', 'x', 'y', 'url', 'accent'].includes(key) ? [] : [value]));
  return [node.text ?? '', ...own, ...(node.content ?? []).map(wordsOf)];
}

function onEdited({ editor: e }) {
  version.value += 1;
  const document = e.getJSON();
  liveDocument.value = document;
  if (JSON.stringify(document) === baseline) return;
  if (['conflict'].includes(saveState.value)) return;
  saveState.value = 'dirty';
  clearTimeout(timer);
  timer = setTimeout(flush, SAVE_AFTER_MS);
}

function flush() {
  clearTimeout(timer);
  if (inFlight) return inFlight.then(() => (saveState.value === 'dirty' ? flush() : undefined));
  if (saveState.value !== 'dirty' && saveState.value !== 'error') return Promise.resolve();

  const document = editor.value.getJSON();
  const sent = JSON.stringify(document);
  saveState.value = 'saving';

  inFlight = contents.saveDocument(openPageId, document, revision.value)
    .then((saved) => {
      revision.value = saved.revision;
      rows.value = saved.rows ?? {};
      written.value = true;
      baseline = sent;
      savedAt.value = new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      saveState.value = JSON.stringify(editor.value.getJSON()) === sent ? 'saved' : 'dirty';
    })
    .catch((e) => {
      const status = e?.response?.status;
      if (status === 409) {
        saveState.value = 'conflict';
      } else if (status === 422) {
        invalidMessage.value = e.response.data?.errors?.document?.[0] ?? 'Something on this page could not be saved.';
        saveState.value = 'invalid';
      } else {
        saveState.value = 'error';
        timer = setTimeout(flush, RETRY_AFTER_MS);
      }
    })
    .finally(() => {
      inFlight = null;
      if (saveState.value === 'dirty') timer = setTimeout(flush, SAVE_AFTER_MS);
    });

  return inFlight;
}

function show(data) {
  revision.value = data.revision;
  rows.value = data.rows ?? {};
  written.value = data.written ?? true;
  locked.value = Boolean(data.locked);
  editor.value.setEditable(!locked.value);
  // Filling the editor is not an edit, so an old page stays untouched until someone changes it.
  editor.value.commands.setContent(data.document, false);
  baseline = JSON.stringify(editor.value.getJSON());
  liveDocument.value = editor.value.getJSON();
  saveState.value = 'saved';
  savedAt.value = null;
  version.value += 1;
}

async function load() {
  loading.value = true;
  loadError.value = false;
  try {
    openPageId = pageId.value;
    await pagesStore.fetchPage(openPageId);
    show(await contents.fetchDocumentForEditor(openPageId));
  } catch {
    loadError.value = true;
  } finally {
    loading.value = false;
  }
}

async function restore(revisionId) {
  await flush();
  try {
    const saved = await contents.restoreDocumentRevision(openPageId, revisionId, revision.value);
    show({ ...saved, locked: locked.value, written: true });
  } catch {
    invalidMessage.value = 'This version could not be restored.';
    saveState.value = 'invalid';
  }
}

function warnBeforeLeaving(event) {
  if (['dirty', 'saving', 'error'].includes(saveState.value)) event.preventDefault();
}

function onShortcut(event) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    toolbar.value?.openLink();
  }
}

watch(pageId, async (next) => {
  if (!next) return;
  await flush();
  load();
});

watch(editor, (e) => { if (e) load(); }, { immediate: true });

onMounted(() => {
  window.addEventListener('beforeunload', warnBeforeLeaving);
  window.addEventListener('keydown', onShortcut);
});

onBeforeUnmount(() => {
  flush();
  window.removeEventListener('beforeunload', warnBeforeLeaving);
  window.removeEventListener('keydown', onShortcut);
});
</script>
